import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import 'react-widgets/styles.css';
import { ROUTE_PATHS as ROUTES } from 'src/routes';
import { isNotEmptyArray } from 'src/services/validationService';
import { push } from 'connected-react-router';
import { getGlobalDataPeriodsData, getGlobalDataSelectedPeriod, getReportDatesData } from 'src/redux/globalData/globalDataReducer';
import { tryGetReportBudgetAnalyticalAccountingByBudgetItems } from 'src/redux/reports/reportsActionCreactor';
import { getReportBudgetAnalyticalAccountingByBudgetItemsIsFetching } from 'src/redux/reports/reportsReducer';
import { clearReportBudgetAnalyticalAccountingByBudgetItems } from 'src/redux/reports/reportsActions';
import { tryGetUserList } from 'src/redux/user/userActionCreator';
import RangeDates from 'src/components/common/RangeDates';
import AppLoading from 'src/components/common/AppLoading';
import { faInfoCircle } from '../../../../node_modules/@fortawesome/free-solid-svg-icons/index';
import BudgetItems from '../BudgetItems';
import { getSubpartialBudgetOfSelectedCreditsIsFetching } from 'src/redux/partialBudget/partialBudgetReducer';
import SelectedBudgetItems from 'src/components/pages/CreditExecution/SelectedBudgetItems';
import { getNewAffectationSelectedCredits } from 'src/redux/affectation/affectationReducer';

const ReportBudgetAnalyticalAccountingByBudgetItems = () => {

    const dispatch = useDispatch();
    const methods = useForm();

    const selectedCredits = useSelector(state => getNewAffectationSelectedCredits(state));
    const selectedCreditsIds = selectedCredits?.map(credit => credit.id);

    const [showMsgReportInProcess, setShowMsgReportInProcess] = useState(false);
    const reportDates = useSelector(state => getReportDatesData(state));

    // Periods
    const periodList = useSelector(state => getGlobalDataPeriodsData(state))?.data?.sort((a, b) => a.year < b.year ? 1 : -1);
    const hasPeriodList = isNotEmptyArray(periodList);
    const globalSelectedPeriod = useSelector(state => getGlobalDataSelectedPeriod(state));

    // Reports redux
    const reportIsFetching = useSelector(state => getReportBudgetAnalyticalAccountingByBudgetItemsIsFetching(state));
    const isFetchingSubpartialBudgetOfSelectedCredits = useSelector(state => getSubpartialBudgetOfSelectedCreditsIsFetching(state));

    const [dateFrom, setDateFrom] = useState(reportDates?.dateFrom);
    const [dateTo, setDateTo] = useState(reportDates?.dateTo);
    const [dateHasErrors, setDateHasErrors] = useState(false);

    useEffect(() => {
        dispatch(tryGetUserList());
        dispatch(clearReportBudgetAnalyticalAccountingByBudgetItems());
    }, [globalSelectedPeriod]);

    const getReport = outputFormat => {
        if (globalSelectedPeriod) {
            setShowMsgReportInProcess(false);
            const params = {
                outputFormat,
                exerciseId: globalSelectedPeriod?.id,
                dateFrom,
                dateTo,
                subCodeIds: selectedCreditsIds
            };
            dispatch(tryGetReportBudgetAnalyticalAccountingByBudgetItems(params)).then(response => {
                if (response?.status == 200) {
                    setShowMsgReportInProcess(true);
                }
            })
        }
    };

    const onClickPDF = () => getReport(1);
    const onClickXLS = () => getReport(2);

    const allowGetReport = (hasPeriodList && !reportIsFetching && dateFrom && dateTo && !dateHasErrors && isNotEmptyArray(selectedCreditsIds));

    return <Container fluid>
        <Card className='mb-3'>
            <Card.Header className='d-flex justify-content-between'>
                <h1 className="h6 mt-1 mb-0">Reporte Analítico de contabilidad por partidas</h1>
                <a className='text-white'
                    target="_blank"
                    href='https://dev.kb.cgmisiones.gob.ar/docs/safi2/operador-servicios/#anal%C3%ADtico-de-contabilidad-de-presupuesto'
                >
                    <FontAwesomeIcon icon={faInfoCircle} className='mr-2' />
                    <small>Ver manual de uso</small>
                </a>
            </Card.Header>
            <Card.Body>
                <Form>
                    <Row>
                        <Col className="offset-sm-2 col-sm-8">
                            <Form.Group>
                                <Form.Label className='text-black-color'>
                                    Ejercicio
                                </Form.Label>
                                <Form.Control
                                    type='number'
                                    name='globalSelectedPeriod'
                                    id='globalSelectedPeriod'
                                    value={globalSelectedPeriod?.year}
                                    readOnly
                                />
                            </Form.Group>
                            <FormProvider {...methods}>
                                <RangeDates
                                    {...{ dateFrom, setDateFrom, dateTo, setDateTo, setDateHasErrors }}
                                />
                            </FormProvider>
                            <BudgetItems
                                isFetchingSubpartialBudgetOfSelectedCredits={isFetchingSubpartialBudgetOfSelectedCredits}
                                showExpedientCheck={false}
                                budgetItemSelectedValidation={false}
                                showBalanceColumn={false}
                            />
                            <SelectedBudgetItems />
                            {showMsgReportInProcess &&
                                <Alert key={'1'} variant={'primary'} className='mt-4'>
                                    <b className='text-black-color' >Aviso:</b> El reporte se esta generando en segundo plano y estará disponible en el módulo de
                                    <a className='font-italic text-info cursor-pointer'
                                        onClick={() => dispatch(push(ROUTES.REPORT_BUDGET_STATUS_CREDIT_EXECUTION_GENERATED))}>
                                        &nbsp;reportes generados.
                                    </a>
                                </Alert>
                            }
                            <Form.Group className='d-flex justify-content-between mt-5'>
                                <Button size='lg' onClick={onClickPDF} disabled={!allowGetReport}>
                                    <FontAwesomeIcon icon={faFilePdf} className='mr-2' />
                                    Generar PDF
                                </Button>
                                {reportIsFetching &&
                                    <Spinner animation='border' />
                                }
                                <AppLoading isLoading={reportIsFetching} />
                                <Button size='lg' onClick={onClickXLS} disabled={!allowGetReport}>
                                    <FontAwesomeIcon icon={faFileExcel} className='mr-2' />
                                    Generar XLS
                                </Button>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Card.Body>
        </Card>
    </Container>
};

export default ReportBudgetAnalyticalAccountingByBudgetItems;